import {
  adminClubsApi,
  type AdminClub,
  type ClubStatus,
} from '@/api/admin/adminClubsApi';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ConfirmDialogAction } from '@/components/admin/ConfirmDialogAction';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { AdminPager } from '@/components/admin/AdminPager';
import { usePageResetOnFilterChange } from '@/hooks/usePageResetOnFilterChange';
import { handleGlobalError } from '@/utils/error-handler';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const PAGE_SIZE = 20;

const AdminClubsPendientesPage = () => {
  const { toast } = useToast();
  // Clubs waiting for review are the default: that is the queue this screen exists for.
  const [status, setStatus] = useState<ClubStatus | ''>('IN_PROGRESS');
  const [page, setPage] = useState(0);
  const [clubs, setClubs] = useState<AdminClub[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingClub, setPendingClub] = useState<AdminClub | null>(null);
  const [isEnabling, setIsEnabling] = useState(false);

  const requestId = useRef(0);

  const load = useCallback(async () => {
    // Switching the status filter twice in a row can leave two fetches in flight.
    // Only the newest may write to state, or a slow earlier response repaints the
    // table with clubs that no longer match the selected filter.
    const current = ++requestId.current;
    setIsLoading(true);
    try {
      const result = await adminClubsApi.list(status, page, PAGE_SIZE);
      if (current !== requestId.current) return;
      setClubs(result.content);
      setTotalPages(result.totalPages);
    } catch (error) {
      if (current !== requestId.current) return;
      handleGlobalError(error, 'No se pudieron cargar los clubes', 'Revisá la conexión e intentá de nuevo.');
    } finally {
      if (current === requestId.current) {
        setIsLoading(false);
      }
    }
  }, [status, page]);

  useEffect(() => {
    load();
  }, [load]);

  usePageResetOnFilterChange(status, page, setPage);

  const enable = async () => {
    if (!pendingClub) return;
    setIsEnabling(true);
    try {
      await adminClubsApi.enable(pendingClub.id);
      toast({ title: `${pendingClub.name} habilitado` });
      await load();
    } catch (error) {
      handleGlobalError(error, 'No se pudo habilitar el club', 'Revisá la auditoría para ver el detalle.');
    } finally {
      setIsEnabling(false);
      setPendingClub(null);
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Clubes</h1>
        <p className="text-sm text-gray-500">
          Habilitación manual para los casos en que ARCA no puede validar los datos
          automáticamente.
        </p>
      </div>

      <select
        value={status}
        onChange={(event) => setStatus(event.target.value as ClubStatus | '')}
        className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm"
      >
        <option value="IN_PROGRESS">En revisión</option>
        <option value="DISABLED">Deshabilitados</option>
        <option value="ENABLED">Habilitados</option>
        <option value="">Todos</option>
      </select>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-12" />
          ))}
        </div>
      ) : clubs.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-500">
          No hay clubes en este estado.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Club</TableHead>
              <TableHead>Dueño</TableHead>
              <TableHead>CUIT</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="w-32" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {clubs.map((club) => (
              <TableRow key={club.id}>
                <TableCell className="font-medium">{club.name}</TableCell>
                <TableCell className="text-gray-600">
                  {club.owners?.length
                    ? club.owners.map((owner, index) => (
                        <span key={owner.pk}>
                          {index > 0 && ', '}
                          {/* The queue's whole point is deciding on a club, and that
                              decision usually means looking at who is behind it. */}
                          <Link
                            to={`/admin/usuarios/${owner.pk}`}
                            className="text-blue-700 hover:underline"
                          >
                            {owner.name || owner.email}
                          </Link>
                        </span>
                      ))
                    : '—'}
                </TableCell>
                <TableCell className="text-gray-600">{club.cuit ?? '—'}</TableCell>
                <TableCell>
                  <Badge variant="outline">{club.status}</Badge>
                </TableCell>
                <TableCell>
                  {club.status !== 'ENABLED' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPendingClub(club)}
                    >
                      Habilitar
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AdminPager
        page={page}
        totalPages={totalPages}
        isLoading={isLoading}
        onPageChange={setPage}
      />

      <AlertDialog
        open={pendingClub !== null}
        onOpenChange={(open) => !open && setPendingClub(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Habilitar {pendingClub?.name}</AlertDialogTitle>
            <AlertDialogDescription>
              El club va a quedar visible y va a poder crear torneos. Confirmá que
              revisaste sus datos fiscales.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isEnabling}>Cancelar</AlertDialogCancel>
            <ConfirmDialogAction
              onConfirm={enable}
              isRunning={isEnabling}
              runningLabel="Habilitando..."
            >
              Habilitar
            </ConfirmDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminClubsPendientesPage;
